import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { CircleAlert, CircleCheck, Info, TriangleAlert, type LucideIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

const alertVariants = cva(
  'relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground',
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground',
        destructive:
          'border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

type EncuentroAlertTone = 'info' | 'warning' | 'danger' | 'positive'

const ENCUENTRO_ALERT_TONES: Record<
  EncuentroAlertTone,
  { icon: LucideIcon; surface: string; iconColor: string }
> = {
  info: { icon: Info, surface: 'bg-en-brand-soft', iconColor: 'text-en-brand' },
  warning: { icon: TriangleAlert, surface: 'bg-en-warning-soft', iconColor: 'text-en-warning' },
  danger: { icon: CircleAlert, surface: 'bg-en-danger-soft', iconColor: 'text-en-danger' },
  positive: { icon: CircleCheck, surface: 'bg-en-positive-soft', iconColor: 'text-en-positive' },
}

type EncuentroAlertProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'title' | 'children'> & {
  look: 'encuentro'
  /** `info` (brand) by default. `danger` is announced as `role="alert"`, the rest as `status`. */
  tone?: EncuentroAlertTone
  title?: React.ReactNode
  body?: React.ReactNode
  /** Buttons (`look="encuentro"`), such as "Revisar" or "Volver a intentar". */
  actions?: React.ReactNode
}

/**
 * The Alumnia UI 1.2.1 alert (docs/redesign/sistema/Alert.md): a notice inside
 * the content, never a full-bleed banner. The actions wrap under the text when
 * the row runs out of room (narrow screens, text at 200 %).
 */
function EncuentroAlert({
  className,
  tone = 'info',
  title,
  body,
  actions,
  ref,
  ...props
}: Omit<EncuentroAlertProps, 'look'> & { ref?: React.Ref<HTMLDivElement> }) {
  const { icon: Icon, surface, iconColor } = ENCUENTRO_ALERT_TONES[tone]
  return (
    <div
      ref={ref}
      data-slot="alert"
      data-look="encuentro"
      data-tone={tone}
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'flex w-full flex-wrap items-start gap-3 rounded-en-lg p-4 font-en-sans text-en-ink',
        surface,
        className
      )}
      {...props}
    >
      <Icon aria-hidden className={cn('mt-0.5 size-5 shrink-0', iconColor)} />
      <div className="grid min-w-0 flex-1 basis-48 gap-0.5">
        {title && <p className="text-en-body-strong">{title}</p>}
        {body && <div className="text-en-meta text-en-ink">{body}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 self-center">{actions}</div>}
    </div>
  )
}

type ClassicAlertProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof alertVariants> & { look?: 'classic' }

const Alert = React.forwardRef<HTMLDivElement, ClassicAlertProps | EncuentroAlertProps>(
  (props, ref) => {
    if (props.look === 'encuentro') {
      const { look: _look, ...rest } = props
      return <EncuentroAlert ref={ref} {...rest} />
    }
    const { className, variant, look: _look, ...rest } = props
    return (
      <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...rest} />
    )
  }
)
Alert.displayName = 'Alert'

const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h5
      ref={ref}
      className={cn('mb-1 font-medium leading-none tracking-tight', className)}
      {...props}
    />
  )
)
AlertTitle.displayName = 'AlertTitle'

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('text-sm [&_p]:leading-relaxed', className)} {...props} />
))
AlertDescription.displayName = 'AlertDescription'

export { Alert, AlertTitle, AlertDescription }
export type { EncuentroAlertTone }
